import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { NavLink, useParams } from "react-router";

import * as Api from "@/api";
import { StackApi, mobileSippeerSchema } from "@/api/stackApi";
import CountryFlag from "@/app/domain/callflow/timetables/holidaysets/calendar/CountryFlag";
import Avatar from "@/design/components/Avatar";
import HeaderCard from "@/design/components/HeaderCard";
import FormGroup from "@/design/forms/FormGroup";
import CountryCodes from "@/resources/countrycode";
import Valkyrie, { viArrowLeft } from "@sippy-platform/valkyrie";

import { IconButton, InputAdornment, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useStore } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAppForm } from "./components/forms/useAppForm";
import LoadingPage from "./components/LoadingPage";
import NotFoundPage from "./components/NotFoundPage";

export default function MobileSippeerSettings() {
  const params = useParams();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const queryClient = useQueryClient();

  const pbx = useSelector((state) => state.domain.pbx);
  const phoneNumberList = useSelector((state) => state.domain.pbx.phoneNumbers);
  const inboundRouteStore = useSelector((state) => state.domain.inboundRoutes);
  const musicOnHoldStore = useSelector((state) => state.domain.musicOnHolds);
  const blocklistStore = useSelector((state) => state.domain.blocklists);
  const distinctiveRingtonesStore = useSelector((state) => state.domain.distinctiveRingtones);

  const outgoingNumberList = useMemo(() => {
    return (
      phoneNumberList?.map((phoneNumber) => ({
        did: phoneNumber.did,
        name: inboundRouteStore.list.find((inbound) => inbound.did === phoneNumber.did)?.name || "",
      })) || []
    );
  }, [inboundRouteStore.list, phoneNumberList]);

  useEffect(() => {
    Promise.all([
      Api.getPbx(),
      Api.getMusicOnHolds(),
      Api.getInbounds(),
      Api.getBlocklists(),
      Api.getDistinctiveRingtones(),
    ]);
  }, []);

  const getMobileSippeer = useQuery({
    queryKey: [params.pbxId, "mobileSippeers", params.mobileSippeerId],
    queryFn: () => StackApi.MobileSippeers.get({ pbxId: params.pbxId, mobileSippeerId: params.mobileSippeerId }),
    enabled: !!(params?.mobileSippeerId && params.mobileSippeerId !== "new"),
    refetchOnWindowFocus: false,
    retry: 2,
  });

  const storeMobileSippeer = useMutation({
    mutationFn: (value) => StackApi.MobileSippeers.put({ pbxId: params.pbxId, mobileSippeer: value }),
    onSuccess: () => {
      Object.keys(form.state.fieldMeta).map((key) => form.setFieldMeta(key, { isDirty: false }));
    },
  });

  // Form
  const form = useAppForm({
    defaultValues: getMobileSippeer.data?.data,
    validators: { onChange: mobileSippeerSchema },
    onSubmit: async ({ value }) => {
      await storeMobileSippeer.mutateAsync(value);
      queryClient.invalidateQueries({ queryKey: [params.pbxId, "mobileSippeers"] });
    },
  });

  // Reset page
  const mobileSippeerId = useStore(form.store, (state) => state.values.id);
  useEffect(() => {
    if (mobileSippeerId !== getMobileSippeer.data?.data.id) form.reset(getMobileSippeer.data?.data);
  }, [form, mobileSippeerId, getMobileSippeer.data?.data]);

  if (getMobileSippeer.isError) return <NotFoundPage message="Mobile user not found" />;
  if (getMobileSippeer.isPending && params.accountId !== "new")
    return <LoadingPage isMainDetail message="Getting mobile user..." />;

  return (
    <form.AppForm>
      <form.Subscribe
        selector={(state) => [state.values.name, state.values.exten]}
        children={([name, exten]) => (
          <>
            <title>{`${name || "Unnamed mobile user"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
            <HeaderCard
              avatar={
                !matches ? (
                  <IconButton variant="avatar" component={NavLink} to="..">
                    <Valkyrie icon={viArrowLeft} />
                    <Avatar text={name} />
                  </IconButton>
                ) : (
                  <Avatar text={name} />
                )
              }
              action={<form.SubmitButton query={storeMobileSippeer} />}
              title={name || "Unnamed mobile user"}
              subheader={exten || "No number set"}
            />
          </>
        )}
      />
      <FormGroup title="Identity" tailwindApi>
        <form.AppField name="name" children={(field) => <field.TextField required label="Name" />} />
        <form.AppField name="exten" children={(field) => <field.TextField disabled label="Number" />} />
        <form.AppField name="email" children={(field) => <field.TextField label="Email address" />} />
        <form.AppField name="postalcode" children={(field) => <field.TextField required label="Postal code" />} />
        <form.AppField
          name="distinctiveRingtoneId"
          children={(field) => (
            <field.AutocompleteField
              label="Distinctive ringtone"
              selects={distinctiveRingtonesStore.list}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.name}
              hasEmpty={null}
            />
          )}
        />
      </FormGroup>
      <FormGroup title="Call flow" tailwindApi>
        <form.AppField
          name="outboundCidNumber"
          children={(field) => (
            <field.AutocompleteField
              label="Outgoing number"
              selects={outgoingNumberList}
              hasEmpty={null}
              getOptionValue={(option) => option.did}
              getOptionLabel={(option) => (option.name ? `${option.did} \u00b7 ${option.name}` : option.did)}
              renderOptionLabel={(option) => (
                <>
                  {option.did}
                  {option.name !== "" && <span style={{ opacity: 0.6 }}>&nbsp;&middot; {option.name}</span>}
                </>
              )}
            />
          )}
        />
        <form.AppField
          name="ringSeconds"
          children={(field) => (
            <field.NumericField
              required
              label="Ring seconds"
              min={1}
              max={120}
              endAdornment={
                <InputAdornment position="end" sx={{ marginRight: 1 }}>
                  seconds
                </InputAdornment>
              }
            />
          )}
        />
        <form.AppField
          name="callRecording"
          children={(field) => (
            <field.SelectField
              label="Record calls"
              selects={[
                { value: "OFF", title: "Off" },
                { value: "INCOMING", title: "Only incoming" },
                { value: "OUTGOING", title: "Only outgoing" },
                { value: "ALL", title: "On" },
              ]}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.title}
            />
          )}
        />
        <form.AppField
          name="moh"
          children={(field) => (
            <field.AutocompleteField
              label="Music on hold"
              selects={musicOnHoldStore.list}
              getOptionValue={(option) => option.name}
              getOptionLabel={(option) => option.name}
            />
          )}
        />
        <form.AppField
          name="countryCallingCode"
          children={(field) => (
            <field.AutocompleteField
              label="Country calling code"
              selects={CountryCodes}
              hasEmpty={null}
              getOptionValue={(option) => Number(option.phone.replace(/^\+/, ""))}
              getOptionLabel={(option) => `${option.phone} \u00b7 ${option.name}`}
              renderOptionLabel={(option) => (
                <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                  <CountryFlag code={option.code} />
                  <span>{`${option.phone} \u00b7 ${option.name}`}</span>
                </Stack>
              )}
              description="Select the country calling code for this user. Leave empty to use the default."
            />
          )}
        />
        <form.AppField
          name="blocklistId"
          children={(field) => (
            <field.AutocompleteField
              label="Blocklist"
              selects={blocklistStore.list.filter((blocklist) => blocklist.type === "user")}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.name}
              hasEmpty={null}
            />
          )}
        />
      </FormGroup>
      <FormGroup title="Overflow" tailwindApi>
        <form.Subscribe
          selector={(state) => state.values.exten}
          children={(exten) => (
            <form.AppField
              name="overflow"
              children={(field) => (
                <field.OverflowField
                  label="Overflow"
                  filter={{ exten: [exten], custom: [], sound: [] }}
                  description="Set the destination if the user does not pick up the call."
                />
              )}
            />
          )}
        />
      </FormGroup>
    </form.AppForm>
  );
}
